/*
Copyright 2026 AB Civil Consulting
Improved Recording Service for Element Call
- Dynamic participant tracking (handles join/leave during recording)
- Proper aspect ratio preservation in grid layout
- Participant name labels
- Screen share detection and priority layout
- Better audio mixing with dynamic track management
*/

import {
    Room,
    Participant,
    Track,
    TrackPublication,
    RoomEvent,
    ParticipantEvent,
    LocalParticipant,
    RemoteParticipant
} from "livekit-client";

export interface RecordingConfig {
    videoBitsPerSecond?: number;
    audioBitsPerSecond?: number;
    mimeType?: string;
    frameRate?: number;
    width?: number;
    height?: number;
    showParticipantNames?: boolean;
    layout?: "grid" | "speaker" | "screenShare";
}

interface ParticipantVideo {
    track: Track; // Store track reference for cleanup
    participant: Participant;
    videoElement: HTMLVideoElement;
    isScreenShare: boolean;
}

interface AudioTrackInfo {
    participantId: string;
    track: MediaStreamTrack;
    sourceNode: MediaStreamAudioSourceNode;
}

export class RecordingService {
    private mediaRecorder: MediaRecorder | null = null;
    private captureStream: MediaStream | null = null;
    private canvas: HTMLCanvasElement;
    private canvasCtx: CanvasRenderingContext2D;
    private audioContext: AudioContext | null = null;
    private audioDestination: MediaStreamAudioDestinationNode | null = null;
    private isRecordingFlag: boolean = false;
    private config: Required<RecordingConfig>;
    private startTime: number = 0;
    private animationFrameId: number | null = null;
    private livekitRoom: Room;
    private roomId: string;
    private roomName: string;

    // Track management
    private audioTracks: Map<string, AudioTrackInfo> = new Map();
    private participantVideos: Map<string, ParticipantVideo> = new Map();

    // Event listeners for cleanup
    private boundHandlers: {
        participantConnected: (p: RemoteParticipant) => void;
        participantDisconnected: (p: RemoteParticipant) => void;
        trackSubscribed: (track: Track, pub: TrackPublication, p: RemoteParticipant) => void;
        trackUnsubscribed: (track: Track, pub: TrackPublication, p: RemoteParticipant) => void;
        localTrackPublished: (pub: TrackPublication, p: LocalParticipant) => void;
        localTrackUnpublished: (pub: TrackPublication, p: LocalParticipant) => void;
    };

    // Callback for sending chunks
    public onChunk: ((base64Chunk: string) => void) | null = null;
    public onError: ((error: Error) => void) | null = null;
    public onParticipantChange: ((count: number) => void) | null = null;
    
    // Track pending FileReader operations to ensure all chunks are processed
    private pendingChunkPromises: Set<Promise<void>> = new Set();

    // Constants for retry limits
    private static readonly VIDEO_READY_MAX_RETRIES = 50; // 50 * 100ms = 5 seconds max wait
    private static readonly VIDEO_READY_RETRY_INTERVAL = 100;

    constructor(livekitRoom: Room, roomId: string, roomName: string, config?: RecordingConfig) {
        this.livekitRoom = livekitRoom;
        this.roomId = roomId;
        this.roomName = roomName;

        this.config = {
            videoBitsPerSecond: config?.videoBitsPerSecond ?? 2500000,
            audioBitsPerSecond: config?.audioBitsPerSecond ?? 128000,
            mimeType: config?.mimeType ?? this.getSupportedMimeType(),
            frameRate: config?.frameRate ?? 30,
            width: config?.width ?? 1920,
            height: config?.height ?? 1080,
            showParticipantNames: config?.showParticipantNames ?? true,
            layout: config?.layout ?? "grid",
        };

        // Create canvas
        this.canvas = document.createElement("canvas");
        this.canvas.width = this.config.width;
        this.canvas.height = this.config.height;
        this.canvasCtx = this.canvas.getContext("2d")!;

        // Bind event handlers
        this.boundHandlers = {
            participantConnected: this.handleParticipantConnected.bind(this),
            participantDisconnected: this.handleParticipantDisconnected.bind(this),
            trackSubscribed: this.handleTrackSubscribed.bind(this),
            trackUnsubscribed: this.handleTrackUnsubscribed.bind(this),
            localTrackPublished: this.handleLocalTrackPublished.bind(this),
            localTrackUnpublished: this.handleLocalTrackUnpublished.bind(this),
        };

        console.log("[RecordingService] Created with config:", this.config);
        console.log("[RecordingService] LiveKit room:", livekitRoom?.name, "state:", livekitRoom?.state);
    }

    private getSupportedMimeType(): string {
        const types = [
            "video/webm;codecs=vp9,opus",
            "video/webm;codecs=vp8,opus",
            "video/webm;codecs=vp9",
            "video/webm;codecs=vp8",
            "video/webm",
        ];
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                console.log("[RecordingService] Using mime type:", type);
                return type;
            }
        }
        return "video/webm";
    }

    // ==================== Event Handlers ====================

    private handleParticipantConnected(participant: RemoteParticipant): void {
        console.log("[RecordingService] Participant connected:", participant.identity);
        this.subscribeToParticipant(participant);
        this.onParticipantChange?.(this.getParticipantCount());
    }

    private handleParticipantDisconnected(participant: RemoteParticipant): void {
        console.log("[RecordingService] Participant disconnected:", participant.identity);
        this.unsubscribeFromParticipant(participant);
        this.onParticipantChange?.(this.getParticipantCount());
    }

    private handleTrackSubscribed(track: Track, publication: TrackPublication, participant: RemoteParticipant): void {
        console.log("[RecordingService] Track subscribed:", track.kind, "from", participant.identity);

        if (track.kind === "video") {
            this.addVideoTrack(participant, track, publication);
        } else if (track.kind === "audio") {
            this.addAudioTrack(participant.identity, track.mediaStreamTrack);
        }
    }

    private handleTrackUnsubscribed(track: Track, publication: TrackPublication, participant: RemoteParticipant): void {
        console.log("[RecordingService] Track unsubscribed:", track.kind, "from", participant.identity);

        if (track.kind === "video") {
            this.removeVideoTrack(participant.identity, track.source);
        } else if (track.kind === "audio") {
            this.removeAudioTrack(participant.identity);
        }
    }

    private handleLocalTrackPublished(publication: TrackPublication, participant: LocalParticipant): void {
        const track = publication.track;
        if (!track) return;

        console.log("[RecordingService] Local track published:", track.kind);

        if (track.kind === "video") {
            this.addVideoTrack(participant, track, publication);
        } else if (track.kind === "audio") {
            this.addAudioTrack(participant.identity, track.mediaStreamTrack);
        }
    }

    private handleLocalTrackUnpublished(publication: TrackPublication, participant: LocalParticipant): void {
        const track = publication.track;
        if (!track) return;

        console.log("[RecordingService] Local track unpublished:", track.kind);

        if (track.kind === "video") {
            this.removeVideoTrack(participant.identity, track.source);
        } else if (track.kind === "audio") {
            this.removeAudioTrack(participant.identity);
        }
    }

    // ==================== Track Management ====================

    private subscribeToParticipant(participant: Participant): void {
        // Subscribe to existing tracks
        for (const [, publication] of participant.videoTrackPublications) {
            if (publication.track) {
                this.addVideoTrack(participant, publication.track, publication);
            }
        }
        for (const [, publication] of participant.audioTrackPublications) {
            if (publication.track?.mediaStreamTrack) {
                this.addAudioTrack(participant.identity, publication.track.mediaStreamTrack);
            }
        }
    }

    private unsubscribeFromParticipant(participant: Participant): void {
        // Remove all tracks from this participant
        const keysToRemove: string[] = [];
        for (const [key] of this.participantVideos) {
            if (key.startsWith(participant.identity + ":")) {
                keysToRemove.push(key);
            }
        }
        // Properly clean up each video track
        keysToRemove.forEach(key => {
            const videoInfo = this.participantVideos.get(key);
            if (videoInfo) {
                try {
                    videoInfo.track.detach(videoInfo.videoElement);
                } catch (e) {
                    console.warn("[RecordingService] Failed to detach track:", e);
                }
                if (videoInfo.videoElement.parentNode) {
                    videoInfo.videoElement.parentNode.removeChild(videoInfo.videoElement);
                }
            }
            this.participantVideos.delete(key);
        });

        this.removeAudioTrack(participant.identity);
    }

    private addVideoTrack(participant: Participant, track: Track, publication: TrackPublication): void {
        const key = `${participant.identity}:${track.source}`;
        
        // Check if we already have this track
        if (this.participantVideos.has(key)) {
            console.log("[RecordingService] Video track already exists:", key);
            return;
        }
        
        console.log("[RecordingService] Adding video track:", key, "source:", track.source);
        
        // FIX: Create our own video element and attach the track directly
        // This ensures we always have a working video element regardless of UI state
        const videoElement = document.createElement("video");
        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.muted = true; // Mute to avoid echo, we capture audio separately
        videoElement.style.display = "none"; // Hidden, just for capture
        document.body.appendChild(videoElement);
        
        // Attach the LiveKit track to our video element
        track.attach(videoElement);
        
        // Wait for video to be ready with retry logic
        let retryCount = 0;
        const maxRetries = RecordingService.VIDEO_READY_MAX_RETRIES;
        const retryInterval = RecordingService.VIDEO_READY_RETRY_INTERVAL;
        
        const checkReady = (): void => {
            if (!this.isRecordingFlag) {
                // Recording stopped, clean up
                track.detach(videoElement);
                if (videoElement.parentNode) {
                    videoElement.parentNode.removeChild(videoElement);
                }
                return;
            }
            
            if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
                console.log("[RecordingService] Video element ready:", key, videoElement.videoWidth, "x", videoElement.videoHeight);
                
                this.participantVideos.set(key, {
                    track,
                    participant,
                    videoElement,
                    isScreenShare: track.source === Track.Source.ScreenShare,
                });
                return;
            }
            
            retryCount++;
            if (retryCount >= maxRetries) {
                console.warn("[RecordingService] Video element not ready after max retries:", key, "- adding anyway");
                // Still add it - video may become ready later during drawing
                this.participantVideos.set(key, {
                    track,
                    participant,
                    videoElement,
                    isScreenShare: track.source === Track.Source.ScreenShare,
                });
                return;
            }
            
            setTimeout(checkReady, retryInterval);
        };
        
        checkReady();
    }

    private removeVideoTrack(participantId: string, source: Track.Source): void {
        const key = `${participantId}:${source}`;
        const videoInfo = this.participantVideos.get(key);
        
        if (videoInfo) {
            // Detach track from video element
            try {
                videoInfo.track.detach(videoInfo.videoElement);
            } catch (e) {
                console.warn("[RecordingService] Failed to detach track:", e);
            }
            // Remove video element from DOM
            if (videoInfo.videoElement.parentNode) {
                videoInfo.videoElement.parentNode.removeChild(videoInfo.videoElement);
            }
        }
        
        this.participantVideos.delete(key);
        console.log("[RecordingService] Removed video track:", key);
    }

    private addAudioTrack(participantId: string, track: MediaStreamTrack): void {
        if (!this.audioContext || !this.audioDestination) {
            console.warn("[RecordingService] Audio context not ready");
            return;
        }

        // Remove existing track for this participant
        this.removeAudioTrack(participantId);

        try {
            const stream = new MediaStream([track]);
            const sourceNode = this.audioContext.createMediaStreamSource(stream);
            sourceNode.connect(this.audioDestination);

            this.audioTracks.set(participantId, {
                participantId,
                track,
                sourceNode,
            });

            console.log("[RecordingService] Added audio track for:", participantId);
        } catch (e) {
            console.error("[RecordingService] Failed to add audio track:", e);
        }
    }

    private removeAudioTrack(participantId: string): void {
        const info = this.audioTracks.get(participantId);
        if (info) {
            try {
                info.sourceNode.disconnect();
            } catch (e) {
                // Ignore disconnect errors
            }
            this.audioTracks.delete(participantId);
            console.log("[RecordingService] Removed audio track for:", participantId);
        }
    }

    private getParticipantCount(): number {
        return 1 + this.livekitRoom.remoteParticipants.size;
    }

    // ==================== Canvas Drawing ====================

    private drawFrame = (): void => {
        if (!this.isRecordingFlag) return;

        const videos = Array.from(this.participantVideos.values());
        const screenShare = videos.find(v => v.isScreenShare);

        // Clear canvas with dark background
        this.canvasCtx.fillStyle = "#1a1a1a";
        this.canvasCtx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        if (videos.length === 0) {
            // No video - show placeholder
            this.drawPlaceholder();
        } else if (screenShare && this.config.layout !== "grid") {
            // Screen share layout: screen share large, others in sidebar
            this.drawScreenShareLayout(screenShare, videos.filter(v => !v.isScreenShare));
        } else if (videos.length === 1) {
            // Single video - full screen with aspect ratio
            this.drawSingleVideo(videos[0]);
        } else {
            // Grid layout
            this.drawGridLayout(videos);
        }

        // Draw recording indicator
        this.drawRecordingIndicator();

        this.animationFrameId = requestAnimationFrame(this.drawFrame);
    };

    private drawPlaceholder(): void {
        this.canvasCtx.fillStyle = "#ffffff";
        this.canvasCtx.font = "bold 48px -apple-system, BlinkMacSystemFont, sans-serif";
        this.canvasCtx.textAlign = "center";
        this.canvasCtx.textBaseline = "middle";
        this.canvasCtx.fillText("Recording in progress...", this.canvas.width / 2, this.canvas.height / 2);

        // Room name
        this.canvasCtx.font = "24px -apple-system, BlinkMacSystemFont, sans-serif";
        this.canvasCtx.fillStyle = "rgba(255, 255, 255, 0.6)";
        this.canvasCtx.fillText(this.roomName, this.canvas.width / 2, this.canvas.height / 2 + 60);
    }

    private drawSingleVideo(video: ParticipantVideo): void {
        const { x, y, width, height } = this.calculateFitDimensions(
            video.videoElement.videoWidth,
            video.videoElement.videoHeight,
            this.canvas.width,
            this.canvas.height
        );

        try {
            this.canvasCtx.drawImage(video.videoElement, x, y, width, height);
        } catch (e) {
            // Video may not be ready
        }

        if (this.config.showParticipantNames) {
            this.drawParticipantName(video.participant.identity, x, y + height - 40, width);
        }
    }

    private drawGridLayout(videos: ParticipantVideo[]): void {
        const count = videos.length;
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        const cellWidth = this.canvas.width / cols;
        const cellHeight = this.canvas.height / rows;
        const padding = 4;

        videos.forEach((video, i) => {
            const col = i % cols;
            const row = Math.floor(i / cols);

            const cellX = col * cellWidth + padding;
            const cellY = row * cellHeight + padding;
            const availableWidth = cellWidth - padding * 2;
            const availableHeight = cellHeight - padding * 2;

            // Calculate fit dimensions within cell
            const { x: offsetX, y: offsetY, width, height } = this.calculateFitDimensions(
                video.videoElement.videoWidth,
                video.videoElement.videoHeight,
                availableWidth,
                availableHeight
            );

            try {
                this.canvasCtx.drawImage(
                    video.videoElement,
                    cellX + offsetX,
                    cellY + offsetY,
                    width,
                    height
                );
            } catch (e) {
                // Draw placeholder for failed video
                this.canvasCtx.fillStyle = "#2a2a2a";
                this.canvasCtx.fillRect(cellX, cellY, availableWidth, availableHeight);
            }

            if (this.config.showParticipantNames) {
                this.drawParticipantName(
                    video.participant.identity,
                    cellX,
                    cellY + availableHeight - 36,
                    availableWidth
                );
            }
        });
    }

    private drawScreenShareLayout(screenShare: ParticipantVideo, participants: ParticipantVideo[]): void {
        // Screen share takes 80% width
        const mainWidth = this.canvas.width * 0.8;
        const sidebarWidth = this.canvas.width * 0.2;

        // Draw screen share
        const { x, y, width, height } = this.calculateFitDimensions(
            screenShare.videoElement.videoWidth,
            screenShare.videoElement.videoHeight,
            mainWidth,
            this.canvas.height
        );

        try {
            this.canvasCtx.drawImage(screenShare.videoElement, x, y, width, height);
        } catch (e) {
            // Ignore
        }

        // Draw participant name for screen share
        this.drawParticipantName(
            `${screenShare.participant.identity}'s screen`,
            0,
            this.canvas.height - 40,
            mainWidth
        );

        // Draw participants in sidebar
        if (participants.length > 0) {
            const thumbHeight = this.canvas.height / Math.min(participants.length, 4);

            participants.slice(0, 4).forEach((video, i) => {
                const thumbY = i * thumbHeight;
                const thumbX = mainWidth;

                const { x: ox, y: oy, width: tw, height: th } = this.calculateFitDimensions(
                    video.videoElement.videoWidth,
                    video.videoElement.videoHeight,
                    sidebarWidth,
                    thumbHeight - 4
                );

                try {
                    this.canvasCtx.drawImage(
                        video.videoElement,
                        thumbX + ox,
                        thumbY + oy + 2,
                        tw,
                        th
                    );
                } catch (e) {
                    // Ignore
                }
            });
        }
    }

    // FIX #3: Enhanced bounds checking with validation for invalid/zero dimensions
    private calculateFitDimensions(
        srcWidth: number,
        srcHeight: number,
        destWidth: number,
        destHeight: number
    ): { x: number; y: number; width: number; height: number } {
        // Validate source dimensions
        if (!srcWidth || !srcHeight || srcWidth <= 0 || srcHeight <= 0 || 
            !Number.isFinite(srcWidth) || !Number.isFinite(srcHeight)) {
            console.warn("[RecordingService] Invalid source dimensions:", srcWidth, srcHeight);
            return { x: 0, y: 0, width: destWidth, height: destHeight };
        }

        // Validate destination dimensions
        if (!destWidth || !destHeight || destWidth <= 0 || destHeight <= 0 ||
            !Number.isFinite(destWidth) || !Number.isFinite(destHeight)) {
            console.warn("[RecordingService] Invalid destination dimensions:", destWidth, destHeight);
            return { x: 0, y: 0, width: 100, height: 100 }; // Safe default
        }

        const srcAspect = srcWidth / srcHeight;
        const destAspect = destWidth / destHeight;

        // Additional safety check for aspect ratio calculation
        if (!Number.isFinite(srcAspect) || !Number.isFinite(destAspect)) {
            return { x: 0, y: 0, width: destWidth, height: destHeight };
        }

        let width: number;
        let height: number;

        if (srcAspect > destAspect) {
            // Source is wider - fit to width
            width = destWidth;
            height = destWidth / srcAspect;
        } else {
            // Source is taller - fit to height
            height = destHeight;
            width = destHeight * srcAspect;
        }

        const x = (destWidth - width) / 2;
        const y = (destHeight - height) / 2;

        return { x, y, width, height };
    }

    private drawParticipantName(name: string, x: number, y: number, maxWidth: number): void {
        // Background
        this.canvasCtx.fillStyle = "rgba(0, 0, 0, 0.6)";
        this.canvasCtx.fillRect(x, y, maxWidth, 32);

        // Text
        this.canvasCtx.fillStyle = "#ffffff";
        this.canvasCtx.font = "14px -apple-system, BlinkMacSystemFont, sans-serif";
        this.canvasCtx.textAlign = "left";
        this.canvasCtx.textBaseline = "middle";

        // Truncate name if too long
        let displayName = name;
        const maxTextWidth = maxWidth - 16;
        while (this.canvasCtx.measureText(displayName).width > maxTextWidth && displayName.length > 3) {
            displayName = displayName.slice(0, -4) + "...";
        }

        this.canvasCtx.fillText(displayName, x + 8, y + 16);
    }

    private drawRecordingIndicator(): void {
        const now = Date.now();
        const blink = Math.floor(now / 500) % 2 === 0;

        // Red recording dot
        this.canvasCtx.fillStyle = blink ? "#ff4444" : "#cc0000";
        this.canvasCtx.beginPath();
        this.canvasCtx.arc(30, 30, 10, 0, Math.PI * 2);
        this.canvasCtx.fill();

        // "REC" text
        this.canvasCtx.fillStyle = "#ffffff";
        this.canvasCtx.font = "bold 14px -apple-system, BlinkMacSystemFont, sans-serif";
        this.canvasCtx.textAlign = "left";
        this.canvasCtx.textBaseline = "middle";
        this.canvasCtx.fillText("REC", 48, 30);

        // Duration
        const duration = this.getDuration();
        const mins = Math.floor(duration / 60000);
        const secs = Math.floor((duration % 60000) / 1000);
        const timeStr = `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
        this.canvasCtx.fillText(timeStr, 80, 30);
    }

    // ==================== Recording Control ====================

    async start(): Promise<boolean> {
        if (this.isRecordingFlag) {
            console.warn("[RecordingService] Already recording");
            return false;
        }

        console.log("[RecordingService] ===== STARTING RECORDING =====");

        // FIX #1: Store audioContext reference for cleanup on failure
        let audioContextCreated: AudioContext | null = null;

        try {
            // Setup audio context
            audioContextCreated = new AudioContext();
            this.audioContext = audioContextCreated;
            this.audioDestination = this.audioContext.createMediaStreamDestination();

            // Subscribe to room events
            this.livekitRoom.on(RoomEvent.ParticipantConnected, this.boundHandlers.participantConnected);
            this.livekitRoom.on(RoomEvent.ParticipantDisconnected, this.boundHandlers.participantDisconnected);
            this.livekitRoom.on(RoomEvent.TrackSubscribed, this.boundHandlers.trackSubscribed);
            this.livekitRoom.on(RoomEvent.TrackUnsubscribed, this.boundHandlers.trackUnsubscribed);
            this.livekitRoom.on(RoomEvent.LocalTrackPublished, this.boundHandlers.localTrackPublished);
            this.livekitRoom.on(RoomEvent.LocalTrackUnpublished, this.boundHandlers.localTrackUnpublished);

            // Subscribe to existing participants
            this.subscribeToParticipant(this.livekitRoom.localParticipant);
            for (const [, participant] of this.livekitRoom.remoteParticipants) {
                this.subscribeToParticipant(participant);
            }

            this.isRecordingFlag = true;
            this.startTime = Date.now();

            // Start drawing frames
            this.drawFrame();

            // Give tracks time to attach
            await new Promise(resolve => setTimeout(resolve, 500));

            // Capture canvas as stream
            const canvasStream = this.canvas.captureStream(this.config.frameRate);
            console.log("[RecordingService] Canvas stream tracks:", canvasStream.getTracks().length);

            // Combine video and audio
            const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];
            const audioTracks = this.audioDestination.stream.getAudioTracks();
            if (audioTracks.length > 0) {
                tracks.push(audioTracks[0]);
            }

            this.captureStream = new MediaStream(tracks);
            console.log("[RecordingService] Combined stream:",
                this.captureStream.getTracks().map(t => `${t.kind}:${t.readyState}`).join(", "));

            // Create MediaRecorder
            this.mediaRecorder = new MediaRecorder(this.captureStream, {
                mimeType: this.config.mimeType,
                videoBitsPerSecond: this.config.videoBitsPerSecond,
                audioBitsPerSecond: this.config.audioBitsPerSecond,
            });

            this.mediaRecorder.ondataavailable = this.handleDataAvailable;
            this.mediaRecorder.onerror = this.handleRecorderError;
            this.mediaRecorder.onstart = () => console.log("[RecordingService] MediaRecorder started");
            this.mediaRecorder.onstop = () => console.log("[RecordingService] MediaRecorder stopped");

            // Start recording with 1 second chunks
            this.mediaRecorder.start(1000);
            console.log("[RecordingService] Recording started!");

            return true;
        } catch (err) {
            console.error("[RecordingService] Start failed:", err);

            // FIX #1: Clean up AudioContext on failure
            if (audioContextCreated && audioContextCreated.state !== "closed") {
                try {
                    await audioContextCreated.close();
                    console.log("[RecordingService] Closed AudioContext after start failure");
                } catch (closeErr) {
                    console.warn("[RecordingService] Failed to close AudioContext:", closeErr);
                }
            }

            this.cleanup();
            this.onError?.(err as Error);
            return false;
        }
    }

    private handleDataAvailable = (event: BlobEvent): void => {
        console.log("[RecordingService] Data available, size:", event.data.size, "type:", event.data.type, "onChunk set:", !!this.onChunk);
        if (event.data.size > 0) {
            // Create a promise to track this chunk conversion
            const chunkPromise = new Promise<void>((resolve) => {
                // Capture onChunk at time of data receipt to avoid race conditions
                const onChunkCallback = this.onChunk;
                if (!onChunkCallback) {
                    console.warn("[RecordingService] No onChunk callback, discarding chunk");
                    resolve();
                    return;
                }
                
                // Use ArrayBuffer approach for more reliable base64 encoding
                const reader = new FileReader();
                reader.onloadend = () => {
                    try {
                        const arrayBuffer = reader.result as ArrayBuffer;
                        if (!arrayBuffer) {
                            console.error("[RecordingService] FileReader returned null");
                            this.pendingChunkPromises.delete(chunkPromise);
                            resolve();
                            return;
                        }
                        
                        // Convert ArrayBuffer to base64
                        const bytes = new Uint8Array(arrayBuffer);
                        let binary = "";
                        const chunkSize = 8192; // Process in chunks to avoid call stack size exceeded
                        for (let i = 0; i < bytes.length; i += chunkSize) {
                            const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
                            binary += String.fromCharCode.apply(null, Array.from(chunk));
                        }
                        const base64 = btoa(binary);
                        
                        console.log("[RecordingService] Chunk converted to base64, length:", base64.length, "from bytes:", bytes.length);
                        
                        if (base64 && base64.length > 0) {
                            onChunkCallback(base64);
                        } else {
                            console.error("[RecordingService] Empty base64 after conversion");
                        }
                    } catch (err) {
                        console.error("[RecordingService] Base64 conversion error:", err);
                    }
                    this.pendingChunkPromises.delete(chunkPromise);
                    resolve();
                };
                reader.onerror = () => {
                    console.error("[RecordingService] Failed to read chunk:", reader.error);
                    this.pendingChunkPromises.delete(chunkPromise);
                    resolve();
                };
                // Use readAsArrayBuffer instead of readAsDataURL for better reliability
                reader.readAsArrayBuffer(event.data);
            });
            
            this.pendingChunkPromises.add(chunkPromise);
        }
    };

    private handleRecorderError = (event: Event): void => {
        console.error("[RecordingService] MediaRecorder error:", event);
        this.onError?.(new Error("MediaRecorder error"));
    };

    // FIX #4: Improved stopAndFinalize to ensure all chunks are captured
    async stopAndFinalize(): Promise<void> {
        if (!this.isRecordingFlag) {
            console.log("[RecordingService] Not recording, nothing to stop");
            return;
        }

        console.log("[RecordingService] ===== STOPPING RECORDING =====");

        this.isRecordingFlag = false;

        // Stop animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Stop MediaRecorder and wait for final data
        return new Promise((resolve) => {
            if (!this.mediaRecorder || this.mediaRecorder.state !== "recording") {
                console.log("[RecordingService] MediaRecorder not active");
                this.cleanup();
                resolve();
                return;
            }

            // FIX #4: Use ondataavailable promise to ensure all data is captured
            let finalDataReceived = false;
            const originalOnDataAvailable = this.mediaRecorder.ondataavailable;

            // Create a promise that resolves when the final data chunk is processed
            const finalDataPromise = new Promise<void>((resolveData) => {
                this.mediaRecorder!.ondataavailable = (event: BlobEvent) => {
                    // Call original handler to process the chunk
                    if (originalOnDataAvailable) {
                        originalOnDataAvailable.call(this.mediaRecorder, event);
                    }
                    
                    console.log("[RecordingService] Final data chunk received, size:", event.data.size);
                    finalDataReceived = true;
                    resolveData();
                };
            });

            this.mediaRecorder.onstop = async () => {
                console.log("[RecordingService] MediaRecorder stopped");

                // Wait for final data to be processed (with timeout)
                if (!finalDataReceived) {
                    const timeoutPromise = new Promise<void>((resolveTimeout) => {
                        setTimeout(() => {
                            console.warn("[RecordingService] Timeout waiting for final data chunk");
                            resolveTimeout();
                        }, 1000);
                    });

                    await Promise.race([finalDataPromise, timeoutPromise]);
                }

                // Wait for all pending FileReader operations to complete
                console.log("[RecordingService] Waiting for", this.pendingChunkPromises.size, "pending chunk(s)...");
                if (this.pendingChunkPromises.size > 0) {
                    await Promise.all([...this.pendingChunkPromises]);
                }
                console.log("[RecordingService] All chunks processed");

                console.log("[RecordingService] Cleaning up after final data processed");
                this.cleanup();
                resolve();
            };

            // Request final data and stop
            try {
                this.mediaRecorder.requestData();
                this.mediaRecorder.stop();
            } catch (e) {
                console.error("[RecordingService] Error stopping MediaRecorder:", e);
                this.cleanup();
                resolve();
            }
        });
    }

    private cleanup(): void {
        console.log("[RecordingService] Cleanup");

        // Remove room event listeners
        this.livekitRoom.off(RoomEvent.ParticipantConnected, this.boundHandlers.participantConnected);
        this.livekitRoom.off(RoomEvent.ParticipantDisconnected, this.boundHandlers.participantDisconnected);
        this.livekitRoom.off(RoomEvent.TrackSubscribed, this.boundHandlers.trackSubscribed);
        this.livekitRoom.off(RoomEvent.TrackUnsubscribed, this.boundHandlers.trackUnsubscribed);
        this.livekitRoom.off(RoomEvent.LocalTrackPublished, this.boundHandlers.localTrackPublished);
        this.livekitRoom.off(RoomEvent.LocalTrackUnpublished, this.boundHandlers.localTrackUnpublished);

        // Stop capture stream
        this.captureStream?.getTracks().forEach((track) => track.stop());
        this.captureStream = null;

        // Disconnect audio nodes
        for (const [, info] of this.audioTracks) {
            try {
                info.sourceNode.disconnect();
            } catch (e) {
                // Ignore
            }
        }
        this.audioTracks.clear();

        // Close audio context
        if (this.audioContext && this.audioContext.state !== "closed") {
            this.audioContext.close().catch(() => {});
        }
        this.audioContext = null;
        this.audioDestination = null;

        // Clean up all video elements
        for (const [, videoInfo] of this.participantVideos) {
            try {
                videoInfo.track.detach(videoInfo.videoElement);
            } catch (e) {
                console.warn("[RecordingService] Failed to detach track during cleanup:", e);
            }
            if (videoInfo.videoElement.parentNode) {
                videoInfo.videoElement.parentNode.removeChild(videoInfo.videoElement);
            }
        }
        this.participantVideos.clear();

        this.mediaRecorder = null;
    }

    isRecording(): boolean {
        return this.isRecordingFlag;
    }

    getDuration(): number {
        return this.isRecordingFlag ? Date.now() - this.startTime : 0;
    }

    dispose(): void {
        console.log("[RecordingService] Dispose");
        if (this.isRecordingFlag) {
            this.isRecordingFlag = false;
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }
            if (this.mediaRecorder?.state === "recording") {
                try {
                    this.mediaRecorder.stop();
                } catch (e) {
                    // Ignore
                }
            }
            this.cleanup();
        }
    }
}
